<template>
  <div class="sprod">
    <div class="sprod__work">
    <section class="sui-card sui-card--flush sprod__catalog">
      <div class="sprod__cond" data-tour="seller-products-toolbar">
        <div class="sprod__cond-head">
          <div class="sprod__cond-caption">
            <span>상태</span>
            <button v-if="hasCondition" type="button" class="sprod__cond-reset" @click="resetConditions">
              전체로
            </button>
          </div>
          <button type="button" class="sui-btn sui-btn--primary sprod__register" data-tour="seller-products-new" @click="openForm()">
            <AppIcon icon="mdi-plus" :size="16" />
            상품 등록
          </button>
        </div>
        <div class="sprod__filters" role="toolbar" aria-label="상태">
          <button
            v-for="card in summary"
            :key="card.key"
            type="button"
            class="sprod__chip"
            :class="{ 'is-active': statusFilter === card.key }"
            @click="statusFilter = card.key"
          >
            {{ card.label }} <strong>{{ card.count }}</strong>
          </button>
        </div>
        <div class="sprod__cond-grid">
          <label class="sprod__field" :class="{ 'is-set': mallFilter !== 'all' }">
            <span>판매 몰</span>
            <select
              class="sui-select"
              data-tour="seller-products-mall"
              aria-label="판매 몰"
              :value="mallFilter"
              @change="onMallChange"
            >
              <option value="all">전체</option>
              <option value="REGULAR">상시몰</option>
              <option value="SALE">공동구매몰</option>
            </select>
          </label>
          <label class="sprod__field" :class="{ 'is-set': category !== 'all' }">
            <span>카테고리</span>
            <select v-model="category" class="sui-select" aria-label="카테고리">
              <option value="all">전체</option>
              <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
            </select>
          </label>
          <label class="sprod__field" :class="{ 'is-set': stockFilter !== 'all' }">
            <span>재고</span>
            <select class="sui-select" aria-label="재고" :value="stockFilter" @change="onStockFilter">
              <option value="all">전체</option>
              <option value="low">저재고 {{ lowCount }}</option>
            </select>
          </label>
          <label class="sprod__field" :class="{ 'is-set': sortKey !== 'newest' }">
            <span>정렬</span>
            <select v-model="sortKey" class="sui-select" aria-label="정렬">
              <option value="newest">최근 등록순</option>
              <option value="sold">판매 많은순</option>
              <option value="stockAsc">재고 적은순</option>
              <option value="priceDesc">판매가 높은순</option>
            </select>
          </label>
          <div class="sprod__field sprod__field--search" :class="{ 'is-set': keyword !== '' }">
            <span>상품명</span>
            <label class="sui-search">
              <AppIcon icon="mdi-magnify" :size="17" />
              <input v-model.trim="keyword" type="search" placeholder="상품명으로 찾기" />
            </label>
          </div>
          <div class="sprod__cond-tools">
            <button
              type="button"
              class="sui-btn"
              data-tour="seller-products-export"
              :disabled="!filtered.length"
              @click="downloadExcel"
            >
              <AppIcon icon="mdi-download" :size="16" />
              CSV
            </button>
            <button
              type="button"
              class="sui-btn sui-btn--ghost"
              :disabled="loading"
              aria-label="새로고침"
              @click="reload"
            >
              <AppIcon icon="mdi-refresh" :size="17" />
            </button>
          </div>
        </div>
      </div>

      <p v-if="errorMessage" class="sprod__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>

      <div v-if="filtered.length" class="sprod__list" data-tour="seller-products-table">
        <div class="sprod__list-head">
          <input
            ref="allBox"
            type="checkbox"
            aria-label="전체 선택"
            :checked="allChecked"
            @change="toggleAll"
          />
          <span>상품</span>
          <span>몰</span>
          <span>판매가</span>
          <span>재고</span>
          <span>상태</span>
        </div>
        <div
          v-for="row in filtered"
          :key="row.id"
          class="sprod__row"
          :class="{
            'is-focus': selected.length < 2 && focused?.id === row.id,
            'is-picked': selected.includes(row.id),
          }"
          role="button"
          tabindex="0"
          :aria-pressed="selected.length < 2 && focused?.id === row.id"
          @click="openRow(row.id)"
          @keydown.enter="onRowKey($event, row.id)"
          @keydown.space="onRowKey($event, row.id)"
        >
          <div class="sprod__cell sprod__cell--check" @click.stop>
            <input type="checkbox" :value="row.id" v-model="selected" :aria-label="row.name" />
          </div>
          <div class="sprod__cell sprod__cell--prod">
            <img v-if="row.image" :src="row.image" :alt="row.name" loading="lazy" />
            <div>
              <p class="sprod__name">{{ row.name }}</p>
              <p class="sprod__meta">{{ row.id }} · {{ row.category }}</p>
            </div>
          </div>
          <div class="sprod__cell sprod__cell--mall">
            <span class="sprod__mall-tag" :class="row.mallKind === 'SALE' ? 'is-sale' : 'is-regular'">
              {{ mallLabel(row.mallKind) }}
            </span>
          </div>
          <div class="sprod__cell sprod__cell--price">{{ formatWon(row.price) }}원</div>
          <div class="sprod__cell sprod__cell--stock" :class="{ 'is-low': row.stock <= LOW_STOCK_THRESHOLD }">
            {{ formatWon(row.stock) }}
          </div>
          <div class="sprod__cell sprod__cell--status">
            <span class="sui-badge" :class="statusBadgeClass(row.status)">
              {{ MALL_PRODUCT_STATUS_LABEL[row.status] }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="sprod__empty">
        <p>
          {{
            loading
              ? '상품을 불러오는 중입니다.'
              : rows.length
                ? '이 조건에 맞는 상품이 없습니다.'
                : '등록된 상품이 없습니다.'
          }}
        </p>
        <button
          v-if="!loading && !errorMessage && !rows.length"
          type="button"
          class="sui-btn sui-btn--primary"
          @click="openForm()"
        >
          첫 상품 등록
        </button>
      </div>
    </section>

    <aside class="sprod__bench" data-tour="seller-products-bench" :aria-label="selected.length > 1 ? `${selected.length}개 선택` : focused ? `${focused.name} 작업` : '상품 작업'">
      <template v-if="selected.length > 1">
        <div class="sprod__pick" data-tour="seller-products-bulk">
          <p class="sprod__pick-kicker">여러 상품</p>
          <h2>{{ selected.length }}개 선택</h2>
          <p class="sprod__pick-note">상태만 한꺼번에 바꿉니다. 수정·미리보기·복제·삭제는 상품을 하나만 열 때 합니다.</p>
          <ul class="sprod__pick-list">
            <li v-for="row in selectedRows" :key="row.id">
              <span>{{ row.name }}</span>
              <em>{{ MALL_PRODUCT_STATUS_LABEL[row.status] }}</em>
            </li>
          </ul>
          <div class="sprod__pick-actions">
            <button
              v-if="selectedRequestable"
              type="button"
              class="sui-btn sui-btn--block sui-btn--primary"
              @click="bulkStatus('REQUESTED')"
            >
              검수 요청하기 {{ requestableCount }}개
            </button>
            <button
              v-if="selectedWithdrawable"
              type="button"
              class="sui-btn sui-btn--block"
              @click="bulkStatus('DRAFT')"
            >
              요청 철회 {{ withdrawableCount }}개
            </button>
            <button v-if="resumeCount" type="button" class="sui-btn sui-btn--block" @click="bulkStatus('SELLING')">
              판매 재개 {{ resumeCount }}개
            </button>
            <button v-if="stopCount" type="button" class="sui-btn sui-btn--block" @click="bulkStatus('HIDDEN')">
              판매 중지 {{ stopCount }}개
            </button>
            <p v-if="blockedResume" class="sprod__pick-none">
              재고가 없는 {{ blockedResume }}개는 판매중으로 되돌리지 않습니다. 재고를 먼저 채우세요.
            </p>
            <p v-if="!hasBulkAction && !blockedResume" class="sprod__pick-none">
              이 선택에는 한꺼번에 바꿀 상태가 없습니다.
            </p>
            <button type="button" class="sui-btn sui-btn--block sui-btn--ghost" @click="selected = []">선택 해제</button>
          </div>
        </div>
      </template>
      <template v-else-if="focused">
        <header class="sprod__focus-head">
          <span class="sui-badge" :class="statusBadgeClass(focused.status)">
            {{ MALL_PRODUCT_STATUS_LABEL[focused.status] }}
          </span>
          <span class="sprod__live" :class="{ 'is-on': isPublic(focused) }">
            {{ mallLiveLabel(focused.status) }}
          </span>
          <span class="sprod__focus-mall">
            {{ mallLabel(focused.mallKind) }}
          </span>
        </header>

        <div class="sprod__focus-id">
          <img v-if="focused.image" :src="focused.image" :alt="focused.name" />
          <div>
            <h2>{{ focused.name }}</h2>
            <p>{{ focused.id }} · {{ focused.category }}</p>
          </div>
        </div>

        <p v-if="focused.rejectReason" class="sprod__reason">반려 사유 · {{ focused.rejectReason }}</p>

        <div class="sprod__focus-price">
          <span>판매가</span>
          <strong>{{ formatWon(focused.price) }}원</strong>
        </div>

        <div class="sprod__focus-stats">
          <button
            type="button"
            data-tour="seller-products-views"
            :aria-label="`${focused.name} 조회수 ${focused.views} · 전환 보기`"
            @click="openViewsInsight(focused)"
          >
            <AppIcon icon="mdi-eye-outline" :size="18" />
            <span>조회</span>
            <strong>{{ formatWon(focused.views) }}</strong>
          </button>
          <button type="button" @click="openViewsInsight(focused)">
            <AppIcon icon="mdi-cart-outline" :size="18" />
            <span>판매</span>
            <strong>{{ formatWon(focused.soldCount) }}</strong>
          </button>
          <button
            type="button"
            data-tour="seller-products-stock"
            :aria-label="`${focused.name} 재고 ${focused.stock}개 · 이력 보기`"
            @click="openStockHistory(focused)"
          >
            <AppIcon icon="mdi-warehouse" :size="18" />
            <span>재고</span>
            <strong :class="{ 'is-low': focused.stock <= LOW_STOCK_THRESHOLD }">{{ formatWon(focused.stock) }}</strong>
          </button>
          <button
            type="button"
            data-tour="seller-products-rating"
            :aria-label="`${focused.name} 평점 ${focused.rating.toFixed(1)} · 후기 ${focused.reviewCount}건`"
            @click="openRating(focused)"
          >
            <AppIcon icon="mdi-star-outline" :size="18" />
            <span>평점</span>
            <strong>{{ focused.rating.toFixed(1) }}</strong>
          </button>
        </div>

        <div class="sprod__focus-status" data-tour="seller-products-mall-live">
          <p class="sprod__focus-label">상태</p>
          <template v-if="canToggleLiveStatus(focused)">
            <select
              class="sui-select"
              :value="focused.status"
              :aria-label="`${focused.name} 판매상태`"
              @change="onStatus(focused, $event)"
            >
              <option v-for="option in LIVE_STATUS_OPTIONS" :key="option" :value="option">
                {{ MALL_PRODUCT_STATUS_LABEL[option] }}
              </option>
            </select>
          </template>
          <template v-else>
            <div class="sprod__focus-now">
              <span class="sui-badge" :class="statusBadgeClass(focused.status)">
                {{ MALL_PRODUCT_STATUS_LABEL[focused.status] }}
              </span>
              <p>{{ reviewStatusNote(focused.status) }}</p>
            </div>
            <button
              v-if="focused.status === 'DRAFT' || focused.status === 'REJECTED'"
              type="button"
              class="sui-btn sui-btn--block sui-btn--primary"
              :disabled="statusBusy === focused.id"
              :aria-label="`${focused.name} ${focused.status === 'REJECTED' ? '검수 다시 요청하기' : '검수 요청하기'}. 현재 상태는 ${MALL_PRODUCT_STATUS_LABEL[focused.status]}입니다.`"
              @click="onRequestReview(focused)"
            >
              {{ statusBusy === focused.id ? '요청 중…' : focused.status === 'REJECTED' ? '검수 다시 요청하기' : '검수 요청하기' }}
            </button>
            <button
              v-else-if="focused.status === 'REQUESTED'"
              type="button"
              class="sui-btn sui-btn--block"
              :disabled="statusBusy === focused.id"
              :aria-label="`${focused.name} 검수 요청 철회. 현재 상태는 검수요청입니다.`"
              @click="onWithdrawReview(focused)"
            >
              {{ statusBusy === focused.id ? '철회 중…' : '요청 철회' }}
            </button>
          </template>
        </div>

        <div class="sprod__focus-actions">
          <button
            type="button"
            class="sui-btn sui-btn--block"
            :class="{ 'sui-btn--primary': canToggleLiveStatus(focused) }"
            @click="openForm(focused.id)"
          >
            수정
          </button>
          <button
            type="button"
            class="sui-btn sui-btn--block"
            data-tour="seller-products-preview"
            @click="openPreview(focused)"
          >
            몰 미리보기
          </button>
          <div class="sprod__focus-more" data-tour="seller-products-clone">
            <button
              type="button"
              class="sui-btn sui-btn--ghost"
              :disabled="cloningId === focused.id"
              @click="onClone(focused)"
            >
              {{ cloningId === focused.id ? '복제 중…' : '복제' }}
            </button>
            <a
              v-if="isPublic(focused)"
              class="sui-btn sui-btn--ghost"
              :href="mallProductPath(focused.id)"
              target="_blank"
              rel="noopener"
            >
              몰에서 보기
            </a>
            <button type="button" class="sui-btn sui-btn--ghost is-danger" @click="onDelete(focused)">삭제</button>
          </div>
        </div>
      </template>
      <div v-else class="sprod__bench-empty">
        <p v-if="loading">상품을 불러오는 중입니다.</p>
        <p v-else-if="!rows.length">등록된 상품이 없습니다. 상품 등록으로 시작합니다.</p>
        <p v-else>이 조건에 맞는 상품이 없습니다.</p>
      </div>
    </aside>
    </div>

    <SellerProductFormModal
      v-if="formOpen"
      :product-code="formCode"
      :persistent="!formCode"
      :initial-mall-kind="'REGULAR'"
      @close="closeForm"
      @saved="onFormSaved"
    />

    <ManageProductStorefrontModal
      v-if="previewCode"
      :code="previewCode"
      audience="seller"
      :mall-open="previewMallOpen"
      :initial-tab="previewTab"
      @close="closePreview"
    />

    <ProductRatingModal
      v-if="ratingInsight"
      :product-code="ratingInsight.code"
      :product-name="ratingInsight.name"
      :rating="ratingInsight.rating"
      :review-count="ratingInsight.reviewCount"
      @close="ratingInsight = null"
    />

    <ProductViewsInsightModal
      v-if="viewsInsight || viewsMenuOpen"
      :product-code="viewsInsight?.code || ''"
      :product-name="viewsInsight?.name || ''"
      :views="viewsInsight?.views || 0"
      :sold-count="viewsInsight?.soldCount || 0"
      :candidates="viewsMenuOpen ? modalCandidates : []"
      @close="closeViews"
      @select="onPickViews"
      @preview="openPreviewFromViews"
    />

    <ProductStockHistoryModal
      v-if="stockHistoryCode || stockMenuOpen"
      :product-code="stockHistoryCode"
      :product-name="stockHistoryName"
      :candidates="stockMenuOpen ? modalCandidates : []"
      audience="seller"
      @close="closeStockHistory"
      @select="onPickStock"
      @updated="onStockUpdated"
    />

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import ManageProductStorefrontModal from '@/components/manage/ManageProductStorefrontModal.vue'
import ProductRatingModal from '@/components/manage/ProductRatingModal.vue'
import ProductStockHistoryModal from '@/components/manage/ProductStockHistoryModal.vue'
import ProductViewsInsightModal from '@/components/manage/ProductViewsInsightModal.vue'
import SellerProductFormModal from '@/components/seller/SellerProductFormModal.vue'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useSellerProducts } from '@/composables/useSellerProducts'
import { mallProductPath } from '@/constants/mall'
import {
  createSellerProduct,
  fetchSellerProduct,
  MALL_PRODUCT_STATUS_LABEL,
  isMallLiveStatus,
  mallLiveLabel,
  type MallProductStatus,
} from '@/api/mallProduct'
import {
  createProductForm,
  hydrateProductForm,
  marginOf,
  toProductPayload,
  type MarginBreakdown,
} from '@/constants/productEditor'
import { LOW_STOCK_THRESHOLD, formatWon } from '@/constants/sellerData'
import { csvEscape } from '@/utils/csv'

type MallFilter = 'REGULAR' | 'SALE'
type MallScope = 'all' | MallFilter

interface ProductRow {
  id: string
  name: string
  image: string
  mallKind: MallFilter
  category: string
  price: number
  cost: number
  margin: MarginBreakdown
  stock: number
  status: MallProductStatus
  approved: boolean
  rejectReason: string
  createdAt: string
  soldCount: number
  views: number
  rating: number
  reviewCount: number
}

const LIVE_STATUS_OPTIONS: MallProductStatus[] = ['SELLING', 'SOLDOUT', 'HIDDEN']

const {
  products: liveProducts,
  loading,
  errorMessage,
  load,
  upsert,
  changeStatus,
  remove,
} = useSellerProducts()

type StatusFilter = MallProductStatus | 'all'

const route = useRoute()
const router = useRouter()
const statusFilter = ref<StatusFilter>('all')
const stockFilter = ref<'all' | 'low'>(String(route.query.stock || '') === 'low' ? 'low' : 'all')
const mallFilter = ref<MallScope>(
  route.query.mall === 'regular' ? 'REGULAR' : route.query.mall === 'group' ? 'SALE' : 'all',
)
const keyword = ref('')
const category = ref<string>('all')
const sortKey = ref<'sold' | 'stockAsc' | 'priceDesc' | 'newest'>('newest')
const selected = ref<string[]>([])
const focusId = ref('')
const formOpen = ref(false)
const formCode = ref('')
const previewCode = ref('')
const previewMallOpen = ref(false)
const previewTab = ref<'detail' | 'review' | 'qna' | 'shipping'>('detail')
const cloningId = ref('')
const stockHistoryCode = ref('')
const stockHistoryName = ref('')
const ratingInsight = ref<{
  code: string
  name: string
  rating: number
  reviewCount: number
} | null>(null)
const viewsInsight = ref<{
  code: string
  name: string
  views: number
  soldCount: number
  mallOpen: boolean
} | null>(null)
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined

function openPreview(row: ProductRow, tab: 'detail' | 'review' | 'qna' | 'shipping' = 'detail') {
  previewTab.value = tab
  previewCode.value = row.id
  previewMallOpen.value = isPublic(row)
}

function closePreview() {
  previewCode.value = ''
  previewMallOpen.value = false
  previewTab.value = 'detail'
}

function openRating(row: ProductRow) {
  viewsInsight.value = null
  stockHistoryCode.value = ''
  stockHistoryName.value = ''
  ratingInsight.value = {
    code: row.id,
    name: row.name,
    rating: row.rating,
    reviewCount: row.reviewCount,
  }
  if (route.query.panel === 'views' || route.query.panel === 'stock' || route.query.code) {
    router.replace({ path: '/seller/products', query: pageQuery({ panel: undefined, code: undefined }) })
  }
}

function openViewsInsight(row: ProductRow) {
  ratingInsight.value = null
  viewsInsight.value = {
    code: row.id,
    name: row.name,
    views: row.views,
    soldCount: row.soldCount,
    mallOpen: isPublic(row),
  }
  if (route.query.panel === 'stock' || route.query.code) {
    stockHistoryCode.value = ''
    stockHistoryName.value = ''
    router.replace({
      path: '/seller/products',
      query: pageQuery({
        panel: route.query.panel === 'views' ? 'views' : undefined,
        code: undefined,
      }),
    })
  }
}

const viewsMenuOpen = computed(() => route.query.panel === 'views')
const stockMenuOpen = computed(() => route.query.panel === 'stock')

function closeViews() {
  viewsInsight.value = null
  if (route.query.panel === 'views') {
    router.replace({ path: '/seller/products', query: pageQuery({ panel: undefined }) })
  }
}

function onPickViews(code: string) {
  const row = rows.value.find((item) => item.id === code)
  if (!row) {
    viewsInsight.value = null
    return
  }
  openViewsInsight(row)
}

function onPickStock(code: string) {
  const row = rows.value.find((item) => item.id === code)
  if (!row) {
    stockHistoryCode.value = ''
    stockHistoryName.value = ''
    return
  }
  openStockHistory(row)
}

function openPreviewFromViews() {
  const item = viewsInsight.value
  if (!item) return
  viewsInsight.value = null
  previewTab.value = 'detail'
  previewCode.value = item.code
  previewMallOpen.value = item.mallOpen
  if (route.query.panel === 'views') {
    router.replace({ path: '/seller/products', query: pageQuery({ panel: undefined }) })
  }
}

function mallLabel(kind: MallFilter): string {
  return kind === 'REGULAR' ? '상시몰' : '공동구매몰'
}

function pageQuery(patch: Record<string, string | undefined> = {}) {
  const query: Record<string, string> = {}
  if (mallFilter.value === 'REGULAR') query.mall = 'regular'
  else if (mallFilter.value === 'SALE') query.mall = 'group'
  if (stockFilter.value === 'low') query.stock = 'low'
  if (route.query.panel) query.panel = String(route.query.panel)
  if (route.query.form) query.form = String(route.query.form)
  if (route.query.code) query.code = String(route.query.code)
  for (const [key, value] of Object.entries(patch)) {
    if (!value) delete query[key]
    else query[key] = value
  }
  return query
}

function setMall(kind: MallScope) {
  if (mallFilter.value === kind) return
  mallFilter.value = kind
  selected.value = []
  category.value = 'all'
  router.replace({ path: '/seller/products', query: pageQuery() })
}

function onMallChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  setMall(value === 'REGULAR' || value === 'SALE' ? value : 'all')
}

function onStockFilter(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  stockFilter.value = value === 'low' ? 'low' : 'all'
  router.replace({ path: '/seller/products', query: pageQuery() })
}

function resetConditions() {
  statusFilter.value = 'all'
  stockFilter.value = 'all'
  category.value = 'all'
  keyword.value = ''
  sortKey.value = 'newest'
  if (mallFilter.value !== 'all') {
    setMall('all')
    return
  }
  router.replace({ path: '/seller/products', query: pageQuery() })
}

function openStockHistory(row: ProductRow) {
  ratingInsight.value = null
  stockHistoryCode.value = row.id
  stockHistoryName.value = row.name
  viewsInsight.value = null
  router.replace({
    path: '/seller/products',
    query: pageQuery({
      code: row.id,
      panel: route.query.panel === 'stock' ? 'stock' : undefined,
    }),
  })
}

function onStockUpdated(payload: { code: string; stock: number; status?: string }) {
  const row = rows.value.find((item) => item.id === payload.code)
  if (!row) return
  row.stock = payload.stock
  if (payload.status) row.status = payload.status as MallProductStatus
  notify(`재고 ${payload.stock.toLocaleString('ko-KR')}개로 반영했습니다.`)
}

function syncFormQuery() {
  const raw = String(route.query.form ?? '').trim()
  if (!raw) {
    formOpen.value = false
    formCode.value = ''
    return
  }
  if (raw === 'new') {
    formOpen.value = true
    formCode.value = ''
    return
  }
  formOpen.value = true
  formCode.value = raw
}

function openForm(code = '') {
  router.replace({ path: '/seller/products', query: pageQuery({ form: code || 'new' }) })
}

function closeForm() {
  router.replace({ path: '/seller/products', query: pageQuery({ form: undefined }) })
}

async function onFormSaved(code: string) {
  closeForm()
  await load(true)
  const saved = liveProducts.value.find((row) => row.code === code)
  const kind: MallFilter = saved?.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE'
  if (saved && mallFilter.value !== 'all' && kind !== mallFilter.value) setMall(kind)
}

onMounted(async () => {
  syncFormQuery()
  await load()
  syncStockQuery()
})

watch(
  () => route.query.form,
  () => syncFormQuery(),
)

watch(
  () => String(route.query.panel || ''),
  (panel, prev) => {
    if (prev === 'views' && panel !== 'views') viewsInsight.value = null
    if (panel === 'views') {
      stockHistoryCode.value = ''
      stockHistoryName.value = ''
    }
  },
)

watch(
  () => route.query.mall,
  (value) => {
    if (value === 'regular') mallFilter.value = 'REGULAR'
    else if (value === 'group') mallFilter.value = 'SALE'
    else mallFilter.value = 'all'
  },
)

watch(
  () => [route.query.stock, route.query.code],
  () => syncStockQuery(),
)

function syncStockQuery() {
  stockFilter.value = String(route.query.stock || '') === 'low' ? 'low' : 'all'
  const code = String(route.query.code || '').trim()
  if (!code) return
  stockHistoryCode.value = code
  const found = liveProducts.value.find((row) => row.code === code)
  stockHistoryName.value = found?.name || ''
}

function closeStockHistory() {
  stockHistoryCode.value = ''
  stockHistoryName.value = ''
  if (route.query.code || route.query.panel === 'stock') {
    router.replace({
      path: '/seller/products',
      query: pageQuery({ code: undefined, panel: undefined }),
    })
  }
}

function reload() {
  selected.value = []
  load(true)
}

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2400)
}

const rows = computed<ProductRow[]>(() =>
  liveProducts.value.map((product) => ({
    id: product.code,
    name: product.name,
    image: product.thumbnail,
    mallKind: product.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE',
    category: product.category,
    price: product.price,
    cost: product.cost ?? 0,
    margin: marginOf({
      price: product.price,
      cost: product.cost,
      marginShare: product.marginShare,
    }),
    stock: product.stock,
    status: product.status,
    approved: Boolean(product.reviewedAt || product.saleStartAt),
    rejectReason: product.status === 'REJECTED' ? product.rejectReason : '',
    createdAt: String(product.createdAt ?? '').slice(0, 10),
    soldCount: product.soldCount,
    views: product.viewCount,
    rating: product.rating,
    reviewCount: product.reviewCount,
  })),
)

const mallRows = computed(() =>
  mallFilter.value === 'all' ? rows.value : rows.value.filter((row) => row.mallKind === mallFilter.value),
)

const categories = computed(() => [...new Set(mallRows.value.map((row) => row.category))])

const lowCount = computed(() => mallRows.value.filter((row) => row.stock <= LOW_STOCK_THRESHOLD).length)

const hasCondition = computed(
  () =>
    statusFilter.value !== 'all' ||
    mallFilter.value !== 'all' ||
    category.value !== 'all' ||
    stockFilter.value !== 'all' ||
    keyword.value !== '' ||
    sortKey.value !== 'newest',
)

const summary = computed<Array<{ key: StatusFilter; label: string; count: number }>>(() => [
  { key: 'all', label: '전체', count: mallRows.value.length },
  { key: 'REQUESTED', label: '검수요청', count: mallRows.value.filter((row) => row.status === 'REQUESTED').length },
  { key: 'SELLING', label: '판매중', count: mallRows.value.filter((row) => row.status === 'SELLING').length },
  { key: 'SOLDOUT', label: '품절', count: mallRows.value.filter((row) => row.status === 'SOLDOUT').length },
  { key: 'REJECTED', label: '반려', count: mallRows.value.filter((row) => row.status === 'REJECTED').length },
  { key: 'HIDDEN', label: '숨김', count: mallRows.value.filter((row) => row.status === 'HIDDEN').length },
  { key: 'DRAFT', label: '임시저장', count: mallRows.value.filter((row) => row.status === 'DRAFT').length },
])

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()

  const list = mallRows.value.filter((row) => {
    if (stockFilter.value === 'low' && row.stock > LOW_STOCK_THRESHOLD) return false
    if (statusFilter.value !== 'all' && row.status !== statusFilter.value) return false
    if (category.value !== 'all' && row.category !== category.value) return false
    if (query && !row.name.toLowerCase().includes(query)) return false
    return true
  })

  const sorted = [...list]
  switch (sortKey.value) {
    case 'sold':
      sorted.sort((a, b) => b.soldCount - a.soldCount)
      break
    case 'stockAsc':
      sorted.sort((a, b) => a.stock - b.stock)
      break
    case 'priceDesc':
      sorted.sort((a, b) => b.price - a.price)
      break
    default:
      sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }
  return sorted
})

const focused = computed(() => filtered.value.find((row) => row.id === focusId.value) || null)

watch(
  filtered,
  (list) => {
    const visible = new Set(list.map((row) => row.id))
    const nextSelected = selected.value.filter((id) => visible.has(id))
    if (nextSelected.length !== selected.value.length) selected.value = nextSelected
    if (list.some((row) => row.id === focusId.value)) return
    focusId.value = list[0]?.id || ''
  },
  { immediate: true },
)

const modalCandidates = computed(() =>
  filtered.value.map((row) => ({ code: row.id, name: row.name })),
)

const allChecked = computed(
  () => filtered.value.length > 0 && filtered.value.every((row) => selected.value.includes(row.id)),
)
const someChecked = computed(() => {
  const visible = filtered.value.filter((row) => selected.value.includes(row.id)).length
  return visible > 0 && visible < filtered.value.length
})
const allBox = ref<HTMLInputElement | null>(null)

watch([someChecked, allBox], () => {
  if (allBox.value) allBox.value.indeterminate = someChecked.value
})

function toggleAll() {
  selected.value = allChecked.value ? [] : filtered.value.map((row) => row.id)
}

function openRow(id: string) {
  selected.value = []
  focusId.value = id
}

function onRowKey(event: KeyboardEvent, id: string) {
  if (event.target !== event.currentTarget) return
  event.preventDefault()
  openRow(id)
}

function isPublic(row: ProductRow): boolean {
  return isMallLiveStatus(row.status)
}

function canToggleLiveStatus(row: ProductRow): boolean {
  return LIVE_STATUS_OPTIONS.includes(row.status)
}

const selectedRows = computed(() => rows.value.filter((row) => selected.value.includes(row.id)))
const requestableCount = computed(
  () => selectedRows.value.filter((row) => row.status === 'DRAFT' || row.status === 'REJECTED').length,
)
const withdrawableCount = computed(() => selectedRows.value.filter((row) => row.status === 'REQUESTED').length)
const selectedRequestable = computed(() => requestableCount.value > 0)
const selectedWithdrawable = computed(() => withdrawableCount.value > 0)
const resumeCount = computed(() => rowsForBulk('SELLING').length)
const stopCount = computed(() => rowsForBulk('HIDDEN').length)
const blockedResume = computed(
  () =>
    selectedRows.value.filter((row) => canToggleLiveStatus(row) && row.status !== 'SELLING' && row.stock <= 0)
      .length,
)
const hasBulkAction = computed(
  () => selectedRequestable.value || selectedWithdrawable.value || resumeCount.value > 0 || stopCount.value > 0,
)

watch(selected, (ids) => {
  if (ids.length === 1) focusId.value = ids[0]
})
const statusBusy = ref('')

function statusBadgeClass(status: MallProductStatus): string {
  if (status === 'REQUESTED') return 'sui-badge--violet'
  if (status === 'REJECTED') return 'sui-badge--red'
  if (status === 'DRAFT') return 'sui-badge--gray'
  return 'sui-badge--gray'
}

function reviewStatusNote(status: MallProductStatus): string {
  if (status === 'DRAFT') return '아직 검수를 요청하지 않았습니다.'
  if (status === 'REJECTED') return '반려된 상품입니다. 내용을 고친 뒤 다시 요청할 수 있습니다.'
  if (status === 'REQUESTED') return '검수를 요청해 둔 상태입니다. 결과를 기다리는 중입니다.'
  return ''
}

function downloadExcel() {
  const list = filtered.value
  if (!list.length) return

  const header = [
    '상품코드',
    '상품명',
    '판매몰',
    '카테고리',
    '판매가',
    '공급가',
    '판매업체마진',
    '재고',
    '상태',
    '몰노출',
    '판매수',
    '후기수',
    '평점',
  ]
  const body = list.map((row) =>
    [
      row.id,
      row.name,
      mallLabel(row.mallKind),
      row.category,
      String(row.price ?? ''),
      String(row.cost ?? ''),
      String(row.margin.seller ?? ''),
      String(row.stock ?? ''),
      MALL_PRODUCT_STATUS_LABEL[row.status] || row.status,
      mallLiveLabel(row.status),
      String(row.soldCount ?? ''),
      String(row.reviewCount ?? ''),
      String(row.rating ?? ''),
    ]
      .map((cell) => csvEscape(cell))
      .join(','),
  )
  const csv = `\uFEFF${[header.join(','), ...body].join('\n')}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `판매상품_${new Date().toISOString().slice(0, 10)}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}

async function onRequestReview(row: ProductRow) {
  if (statusBusy.value) return
  const again = row.status === 'REJECTED'
  if (
    !window.confirm(
      again
        ? `'${row.name}' 상품의 검수를 다시 요청할까요?`
        : `'${row.name}' 상품의 검수를 요청할까요? 요청 후 관리자 상품관리에 검수요청으로 보입니다.`,
    )
  ) {
    return
  }
  statusBusy.value = row.id
  try {
    await changeStatus(row.id, 'REQUESTED')
    notify(again ? '검수를 다시 요청했습니다.' : '검수를 요청했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '검수를 요청하지 못했습니다.')
  } finally {
    statusBusy.value = ''
  }
}

async function onWithdrawReview(row: ProductRow) {
  if (statusBusy.value) return
  if (!window.confirm(`'${row.name}' 검수 요청을 철회하고 임시저장으로 되돌릴까요?`)) return
  statusBusy.value = row.id
  try {
    await changeStatus(row.id, 'DRAFT')
    notify('검수 요청을 철회했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '검수 요청을 철회하지 못했습니다.')
  } finally {
    statusBusy.value = ''
  }
}

async function onStatus(row: ProductRow, event: Event) {
  const status = (event.target as HTMLSelectElement).value as MallProductStatus
  if (status === row.status) return
  if (!canToggleLiveStatus(row) || !LIVE_STATUS_OPTIONS.includes(status)) {
    notify('목록에서는 판매중·품절·숨김만 바꿀 수 있습니다. 검수 요청은 수정 화면에서 해 주세요.')
    await reload()
    return
  }
  if (
    !window.confirm(
      `'${row.name}' 판매상태를 '${MALL_PRODUCT_STATUS_LABEL[status]}'으로 바꿀까요?`,
    )
  ) {
    await reload()
    return
  }

  try {
    await changeStatus(row.id, status)
    notify(`판매상태를 '${MALL_PRODUCT_STATUS_LABEL[status]}'으로 바꿨습니다.`)
  } catch (error) {
    notify(error instanceof Error ? error.message : '판매상태를 바꾸지 못했습니다.')
    await reload()
  }
}

function rowsForBulk(status: MallProductStatus): ProductRow[] {
  return selectedRows.value.filter((row) => {
    if (status === 'REQUESTED') return row.status === 'DRAFT' || row.status === 'REJECTED'
    if (status === 'DRAFT') return row.status === 'REQUESTED'
    if (status === 'SELLING') return canToggleLiveStatus(row) && row.status !== 'SELLING' && row.stock > 0
    if (status === 'HIDDEN') return canToggleLiveStatus(row) && row.status !== 'HIDDEN'
    return false
  })
}

async function bulkStatus(status: MallProductStatus) {
  const targets = rowsForBulk(status)
  if (!targets.length) {
    notify(
      status === 'REQUESTED'
        ? '임시저장·반려 상품만 검수를 요청할 수 있습니다.'
        : status === 'DRAFT'
          ? '검수요청 상품만 철회할 수 있습니다.'
          : '판매중·품절·숨김 상품만 일괄로 바꿀 수 있습니다.',
    )
    return
  }
  const label =
    status === 'REQUESTED' ? '검수요청' : status === 'DRAFT' ? '임시저장' : MALL_PRODUCT_STATUS_LABEL[status]
  const skipped = selectedRows.value.length - targets.length
  const scope = skipped > 0 ? `바꿀 수 있는 ${targets.length}개` : `${targets.length}개`
  if (!window.confirm(`${scope} 상품을 '${label}'으로 바꿀까요?`)) return

  let done = 0
  let failed = ''
  for (const row of targets) {
    try {
      await changeStatus(row.id, status)
      done += 1
    } catch (error) {
      if (!failed) failed = error instanceof Error ? error.message : '일부 상품은 바꾸지 못했습니다.'
    }
  }

  notify(
    failed
      ? `${done}개를 바꿨습니다. ${failed}`
      : `${done}개 상품을 '${label}'으로 바꿨습니다.`,
  )
  selected.value = []
}

async function onDelete(row: ProductRow) {
  if (!window.confirm(`'${row.name}' 상품을 삭제할까요? 함우리몰에서도 내려갑니다.`)) {
    return
  }

  try {
    await remove(row.id)
    notify('상품을 삭제했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '상품 삭제에 실패했습니다.')
  }
}

async function onClone(row: ProductRow) {
  if (cloningId.value) return

  const ok = await confirmDialog({
    title: '상품 복제',
    message: `'${row.name}'을(를) 복제해 임시저장 할까요?`,
    detail: '상품명에 「(복제)」가 붙고, 이미지는 기존 URL을 그대로 씁니다.',
    confirmLabel: '복제',
    tone: 'primary',
  })
  if (!ok) return

  cloningId.value = row.id
  try {
    const product = await fetchSellerProduct(row.id)
    const form = createProductForm({
      category: product.category,
      shippingFee: product.shippingFee,
      freeShipOver: 0,
    })
    hydrateProductForm(form, product)
    form.name = `${product.name} (복제)`
    form.requestNote = ''

    const created = await createSellerProduct(toProductPayload(form, 'DRAFT'))
    upsert(created)
    notify('복제한 상품을 임시저장했습니다.')
    openForm(created.code)
  } catch (error) {
    notify(error instanceof Error ? error.message : '상품 복제에 실패했습니다.')
  } finally {
    cloningId.value = ''
  }
}
</script>

<style lang="scss" src="./SellerProductsPage.scss"></style>
